'use client'

import { useEffect, useState } from 'react'
import { SunIcon, MoonIcon, ComputerDesktopIcon } from '@heroicons/react/24/outline'
import { motion, AnimatePresence } from 'framer-motion'
import { useTheme } from '@/app/[locale]/ThemeProvider'

const iconProps = { className: "w-6 h-6" }

const icons = {
  light: <SunIcon {...iconProps} />, 
  dark: <MoonIcon {...iconProps} />,
  system: <ComputerDesktopIcon {...iconProps} />
}

type ThemeKey = keyof typeof icons;
const themeCycle: ThemeKey[] = ['light', 'dark'];

// This component is only loaded dynamically when ThemeContext is available
export default function ThemeSwitcherWithContext() {
  // Add mounted state to prevent hydration mismatch
  const [mounted, setMounted] = useState(false);
  
  // Safe to use the hook here since this component is only loaded
  // when we know ThemeContext is available
  const { theme, themePreference, setTheme } = useTheme();
  
  // Set mounted state after initial render
  useEffect(() => {
    setMounted(true);
  }, []);
  
  const cycleTheme = () => {
    // If currently using system preference (initial state), switch to light
    if (themePreference === 'system') {
      setTheme('light');
      return;
    }
    
    const currentIndex = themeCycle.indexOf(themePreference as ThemeKey);
    const nextIndex = (currentIndex + 1) % themeCycle.length;
    const nextThemePreference = themeCycle[nextIndex];
    setTheme(nextThemePreference);
  };

  // Render a static button until client-side hydration is complete
  if (!mounted) {
    return (
      <button
        className="relative flex items-center justify-center w-8 h-8 rounded-full bg-bg-main/50 text-text-main/80"
        aria-label="Theme switcher loading"
      >
        <span className="sr-only">Loading theme</span>
        <span className="absolute">
          {/* Static icon (no animation) */}
          {icons[theme as ThemeKey] || icons['dark']}
        </span>
      </button>
    );
  }

  // Full interactive button with animations after hydration
  return (
    <button
      onClick={cycleTheme}
      className="relative flex items-center justify-center w-8 h-8 rounded-full text-text-main hover:text-primary transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-bg-main group"
      aria-label={`Switch theme (current: ${themePreference}, active: ${theme})`}
      title={`Switch theme (current: ${themePreference}, active: ${theme})`}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={theme}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.2 }}
          className="absolute group-hover:scale-110 transition-transform duration-200"
        >
          {icons[theme as ThemeKey] || icons['system']}
        </motion.span>
      </AnimatePresence>
    </button>
  );
} 